import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, startTransition } from 'react'
import { HistoryCache } from '../lib/idb-cache'
import { useMarketStore } from '../services/store'
import { API_CONFIG } from '../config/constants'
import { marketDataBus } from '../services/MarketDataBus'
import { getFirebaseAuth } from '../services/firebase'
import { useSettingsStore } from '../stores/settingsStore'
import { useCogEngine } from '../hooks/useCogEngine'
import { useMarketWatchEngine } from '../hooks/useMarketWatchEngine'
import { usePrecomputeEngine } from '../hooks/usePrecomputeEngine'
import { useAuth } from '../auth/AuthContext'


// FREEZE FIX: Stable singleton for empty results.
// Returning `[]` creates a NEW reference every call, causing React Query
// to think data changed → triggers re-renders → Zustand updates → freeze.
// Using a frozen singleton ensures referential stability.
const EMPTY_HISTORY: any[] = Object.freeze([]) as unknown as any[]

// --- Web Worker Setup ---
// Instantiate a single global worker instance for the app lifecycle
import HistoryWorker from '../workers/history.worker.ts?worker';

let _historyWorker: Worker | null = null;
export const getHistoryWorker = () => {
    if (typeof window === 'undefined') return null; // SSR safety
    if (!_historyWorker) {
        _historyWorker = new HistoryWorker();
    }
    return _historyWorker;
};

// Helper to wrap postMessage in a Promise
export const runInWorker = (type: string, payload: any): Promise<any> => {
    return new Promise((resolve, reject) => {
        const worker = getHistoryWorker();
        if (!worker) return reject(new Error('No worker available'));
        
        const id = Math.random().toString(36).substring(2, 15);
        
        let chunks: any[] = [];
        let isChunked = false;
        let baseResult: any = null;
        
        const handleMessage = (e: MessageEvent) => {
            if (e.data.id === id) {
                if (e.data.type === 'CHUNK') {
                    isChunked = true;
                    if (e.data.chunkIndex === 0 && e.data.baseResult) {
                        baseResult = e.data.baseResult;
                    }
                    if (e.data.data) {
                        chunks.push(...e.data.data);
                    }
                    // Traditional single-array chunking
                    if (e.data.totalChunks > 0 && e.data.chunkIndex === e.data.totalChunks - 1) {
                        worker.removeEventListener('message', handleMessage);
                        const result = baseResult ? { ...baseResult, data: chunks } : chunks;
                        setTimeout(() => resolve(result), 0);
                    }
                } else if (e.data.type === 'CHUNK_PART') {
                    // For complex object chunks (e.g. MULTIPART)
                    if (baseResult && e.data.partKey) {
                        const keys = e.data.partKey.split('.');
                        let target = baseResult;
                        for (let i = 0; i < keys.length - 1; i++) {
                            target = target[keys[i]];
                        }
                        const lastKey = keys[keys.length - 1];
                        if (!Array.isArray(target[lastKey])) target[lastKey] = [];
                        target[lastKey].push(...e.data.data);
                    }
                } else if (e.data.type === 'SUCCESS_MULTIPART') {
                    worker.removeEventListener('message', handleMessage);
                    setTimeout(() => resolve(baseResult), 0);
                } else if (e.data.type === 'SUCCESS') {
                    worker.removeEventListener('message', handleMessage);
                    if (e.data.encoding === 'json-string' && typeof e.data.data === 'string') {
                        // PERF FIX: JSON.parse on large payloads (320+ records) causes 1.5s main-thread freeze.
                        // The worker should NEVER use json-string encoding for large results.
                        // If this path triggers, it's a bug in the worker — reject so we can fix it.
                        console.error('[runInWorker] Received json-string encoding — this blocks the main thread. Fix the worker to use raw encoding.');
                        // Still parse it as a fallback, but schedule it across frames via chunked parsing
                        setTimeout(() => {
                            try { resolve(JSON.parse(e.data.data)); }
                            catch (err) { reject(err); }
                        }, 0);
                    } else {
                        // Native object via Structured Clone — zero parsing cost
                        setTimeout(() => resolve(e.data.data), 0);
                    }
                } else if (e.data.type === 'ERROR') {
                    worker.removeEventListener('message', handleMessage);
                    reject(new Error(e.data.error || 'Unknown worker error'));
                }
            }
        };
        
        worker.addEventListener('message', handleMessage);
        worker.postMessage({ id, type, payload });
    });
};

/**
 * GlobalHistoryFetcher — SINGLE source of truth for Intraday History data.
 *
 * Fetches history via direct API endpoints (no tRPC) every 60s (if live) or once (if historical).
 * Syncs to Zustand store so ALL tables, charts, and cards read from the same data.
 *
 * Fallback order for Intraday: KV → D1 → CSV (handled server-side)
 */
export function GlobalHistoryFetcher() {
    const { isAuthenticated, loading, firebaseUser } = useAuth()
    const symbol = useSettingsStore((state) => state.symbol)
    const expiry = useSettingsStore((state) => state.expiry)
    const baseTime = useSettingsStore((state) => state.baseTime)
    const range = useSettingsStore((state) => state.range)
    const mode = useSettingsStore((state) => state.mode)
    const isLive = useSettingsStore((state) => state.isLive)
    const historicalDate = useSettingsStore((state) => state.historicalDate)
    const inspectionSource = useSettingsStore((state) => state.inspectionSource)
    const rangeStartDate = useSettingsStore((state) => state.rangeStartDate)
    const rangeEndDate = useSettingsStore((state) => state.rangeEndDate)

    // BACKGROUND MATHEMATICAL ENGINE: Generates CoG globally    // Background Analytical Engines
    useCogEngine()
    useMarketWatchEngine()
    
    // AOT PRECOMPUTATION ENGINE: Computes timeline for buttery smooth 0ms playback
    usePrecomputeEngine()

    const queryClient = useQueryClient()
    
    // Initialize DuckDB WASM in the worker asynchronously as early as possible
    useEffect(() => {
        runInWorker('INIT_DUCKDB', {}).catch(e => {
            console.error('[GlobalHistoryFetcher] Failed to initialize DuckDB', e);
        });
    }, []);

    // FREEZE FIX: Guard to prevent repeated CSV worker execution.
    // Without this, every time React Query re-runs the queryFn (on refetch, on tab focus, etc.),
    // the worker is triggered again even though the CSV data hasn't changed.
    const csvFallbackTriggeredRef = useRef(false)
    // Reset the guard when symbol or expiry changes (new data expected)
    const csvFallbackKeyRef = useRef(`${symbol}|${expiry}`)
    if (csvFallbackKeyRef.current !== `${symbol}|${expiry}`) {
        csvFallbackKeyRef.current = `${symbol}|${expiry}`
        csvFallbackTriggeredRef.current = false
    }

    // Track previous settings to detect user-driven changes and force refetch
    const prevSettings = useRef({
        symbol,
        expiry,
        isLive,
        historicalDate,
        inspectionSource,
        rangeStartDate,
        rangeEndDate,
    })

    useEffect(() => {
        const prev = prevSettings.current
        const changed =
            prev.symbol !== symbol ||
            prev.expiry !== expiry ||
            prev.isLive !== isLive ||
            prev.historicalDate !== historicalDate ||
            prev.inspectionSource !== inspectionSource ||
            prev.rangeStartDate !== rangeStartDate ||
            prev.rangeEndDate !== rangeEndDate

        if (changed) {
            // ---> CLEAR STALE DATA IMMEDIATELY <---
            // If symbol changed, clear the old one. If not, clear the current one.
            // This immediately drops the tables/charts into a "loading" or "empty" state 
            // so the user visually sees that new data is fetching, preventing them from
            // manually refreshing the page out of confusion.
            // FREEZE FIX: Replaced startTransition with setTimeout to prevent React 18 'Expected static flag was missing'
            // tearing errors caused by mutating external Zustand stores during a concurrent transition.
            setTimeout(() => {
                useMarketStore.getState().clearHistoryForSymbol(prev.symbol)
                if (symbol !== prev.symbol) {
                    useMarketStore.getState().clearHistoryForSymbol(symbol)
                }
            }, 0)

            console.log(`[GlobalHistoryFetcher] Settings changed, invalidating history queries`)
            queryClient.invalidateQueries({
                queryKey: ['global-history'],
            })
            prevSettings.current = {
                symbol,
                expiry,
                isLive,
                historicalDate,
                inspectionSource,
                rangeStartDate,
                rangeEndDate,
            }
        }
    }, [
        symbol,
        expiry,
        isLive,
        historicalDate,
        inspectionSource,
        rangeStartDate,
        rangeEndDate,
        queryClient,
    ])

    const { data } = useQuery({
        queryKey: [
            'global-history',
            symbol,
            expiry,
            inspectionSource,
            historicalDate,
            rangeStartDate,
            rangeEndDate,
            isLive,
            !!firebaseUser,
        ],
        queryFn: async () => {
            if (!symbol) return null
            if (typeof window !== 'undefined' && window.location.pathname.includes('/backtest')) {
                return EMPTY_HISTORY; // Disable fetching on backtest page
            }

            // Mode 1: Current Intraday (KV → D1 → CSV fallback handled server-side)
            if (inspectionSource === 'CurrentIntraday') {
                console.log(`[GlobalHistoryFetcher] Fetching intraday history for ${symbol} expiry: ${expiry}`)
                const params = new URLSearchParams({ symbol })
                if (expiry) params.set('expiry', expiry)
                params.set('t', Date.now().toString())
                
                try {
                    const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || ""
                    const resp = await fetch(`/api/intraday-history-v2?${params}`, { 
                        cache: 'no-store',
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined
                    })

                    if (resp.ok) {
                        const data = await resp.json()
                        const errHeader = resp.headers.get('X-Error')
                        if (errHeader) {
                            console.warn(`[GlobalHistoryFetcher] API Warning/Error: ${errHeader}`)
                        }
                        console.log(`[GlobalHistoryFetcher] Retrieved ${data?.length || 0} rows from Server`)
                        
                        // If API succeeded and returned data, we are done
                        if (data && data.length > 0) return data
                        
                        console.warn(`[GlobalHistoryFetcher] API returned empty array, falling back to direct client-side CSV fetch`)
                    } else {
                        console.error(`[GlobalHistoryFetcher] Intraday history fetch failed: ${resp.status} - ${resp.statusText}`)
                    }
                } catch (err: any) {
                    console.error(`[GlobalHistoryFetcher] API Fetch failed (${err.message}), falling back to direct client-side CSV fetch...`)
                }

                // --- ABSOLUTE FALLBACK: CLIENT-SIDE CSV FETCH ---
                // If the Server API (Cloudflare Pages) fails or times out (503), the browser fetches directly
                const INTRADAY_SHEET_LINKS = API_CONFIG.SOURCES.INTRADAY_SHEET_LINKS

                const sClean = symbol.replace(/[^A-Z0-9]/g, '')
                const canonicalSym = (sClean === 'NIFTY' || sClean.includes('NIFTY')) ? 'NIFTY' :
                                     (sClean === 'SENSEX' || sClean.includes('SENSEX')) ? 'SENSEX' : symbol

                const csvUrl = (INTRADAY_SHEET_LINKS as any)[canonicalSym]
                if (!csvUrl) return EMPTY_HISTORY

                try {
                     // Helper: DD-MM-YYYY to YYYY-MM-DD
                     const altExpiry = expiry ? (
                        /^\d{2}-\d{2}-\d{4}$/.test(expiry) ? `${expiry.split('-')[2]}-${expiry.split('-')[1]}-${expiry.split('-')[0]}` :
                        /^\d{4}-\d{2}-\d{2}$/.test(expiry) ? `${expiry.split('-')[2]}-${expiry.split('-')[1]}-${expiry.split('-')[0]}` : ''
                     ) : ''

                     // We MUST allow repeated worker execution because the Google Sheet
                     // is updated continuously with new live intraday data every minute.
                     
                     console.log(`[GlobalHistoryFetcher] Offloading Intraday CSV Fetch & Parse to Web Worker...`)
                     
                     // Use a cache-buster so we get the fresh live data from the Google Sheet every minute
                     const liveCsvUrl = `${csvUrl}&_t=${Date.now()}`;
                     
                     const resultObj = await runInWorker('FETCH_AND_PARSE_CSV_INTRADAY', {
                         csvUrl: liveCsvUrl,
                         canonicalSym,
                         expiry: expiry || '',
                         altExpiry
                     });
                     
                     // Handle both old array return and new object return { data, finalExpiryList, ... }
                     const groupedRows = Array.isArray(resultObj) ? resultObj : (resultObj.data || []);

                     console.log(`[GlobalHistoryFetcher] Client-side CSV fallback succeeded via Worker. Grouped ${groupedRows.length} snapshots.`)
                     // FREEZE FIX: Return stable singleton when empty to prevent
                     // reference instability from triggering re-render loops.
                     return groupedRows.length > 0 ? groupedRows : EMPTY_HISTORY
                } catch (fallbackErr: any) {
                     console.error(`[GlobalHistoryFetcher] Client-side CSV fallback failed:`, fallbackErr?.message)
                     return EMPTY_HISTORY
                }
            }

            // Mode 2: Historical Snapshot (Single Day from Hugging Face -> Fallback D1)
            if (inspectionSource === 'HistoricalSnapshot' && historicalDate) {
                const sClean = symbol.replace(/[^A-Z0-9]/g, '')
                const canonicalSym = (sClean === 'NIFTY' || sClean.includes('NIFTY')) ? 'NIFTY' :
                                     (sClean === 'SENSEX' || sClean.includes('SENSEX')) ? 'SENSEX' : symbol

                const hfUrl = `/api/history-proxy?symbol=${canonicalSym}&date=${historicalDate}&type=parquet`

                try {
                    let groupedRows: any[] = [];
                    let finalExpiryList: string[] = [];
                    let autoSelectedExpiry: string | null = null;

                    let fetchFromDb = false;
                    try {
                        const hfToken = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                        console.log(`[GlobalHistoryFetcher] Offloading HF Fetch & Parse to Web Worker -> ${hfUrl}`)
                        
                        const workerResult = await runInWorker('FETCH_AND_PARSE_PARQUET_HF', {
                            hfUrl,
                            hfToken,
                            canonicalSym,
                            historicalDate,
                            expiry: expiry || ''
                        });
                        
                        groupedRows = workerResult.data;
                        finalExpiryList = workerResult.finalExpiryList;
                        autoSelectedExpiry = workerResult.autoSelectedExpiry;
                        useSettingsStore.getState().setDatasetDate(workerResult.datasetDate || null);

                        if (groupedRows.length === 0) {
                            console.log(`[GlobalHistoryFetcher] HF Parquet returned 0 snapshots for ${canonicalSym}/${historicalDate}. Trying DB fallback...`);
                            fetchFromDb = true;
                        }
                    } catch (hfErr: any) {
                        const msg = hfErr.message || '';
                        if (msg.includes('not found') || msg.includes('not_found') || msg.includes('HF_NOT_FOUND')) {
                            console.log(`[GlobalHistoryFetcher] Parquet file not present on HF (${canonicalSym}/${historicalDate}). Switching to DB fallback...`);
                        } else {
                            console.log(`[GlobalHistoryFetcher] HF Parquet loading fallback (${msg}). Trying DB fallback...`);
                        }
                        fetchFromDb = true;
                    }

                    if (fetchFromDb) {
                        const params = new URLSearchParams({
                            symbol: canonicalSym,
                            date: historicalDate,
                        });
                        const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                        let dbData: any[] = [];
                        try {
                            const dbResp = await fetch(`/api/historical?${params}`, {
                                headers: token ? { Authorization: `Bearer ${token}` } : undefined
                            });
                            if (dbResp.ok) {
                                dbData = await dbResp.json();
                            }
                        } catch (e) {
                            console.warn('[GlobalHistoryFetcher] /api/historical fetch failed:', e);
                        }

                        // If D1 / historical vault returned 0 snapshots (e.g. local dev or recent date), fallback to live intraday feed
                        if (!Array.isArray(dbData) || dbData.length === 0) {
                            console.log(`[GlobalHistoryFetcher] DB historical returned 0 snapshots for ${canonicalSym}. Switching to Intraday History CSV fallback...`);
                            try {
                                const intraResp = await fetch(`/api/intraday-history-v2?symbol=${canonicalSym}&expiry=${expiry || ''}`, {
                                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                                });
                                if (intraResp.ok) {
                                    dbData = await intraResp.json();
                                }
                            } catch (e) {
                                console.error('[GlobalHistoryFetcher] Intraday fallback failed:', e);
                            }
                        }

                        if (Array.isArray(dbData) && dbData.length > 0) {
                            console.log(`[GlobalHistoryFetcher] DB/Intraday fallback returned ${dbData.length} snapshots.`);
                            groupedRows = dbData;
                            // Extrapolate and sort finalExpiryList from DB records so MIN expiry is first
                            const expiries = Array.from(new Set(dbData.flatMap((r: any) => {
                                if (r.expiry) return [r.expiry];
                                if (r.optionsData) {
                                    const sym = Object.keys(r.optionsData)[0];
                                    if (sym && r.optionsData[sym]) return Object.keys(r.optionsData[sym]);
                                }
                                return [];
                            }).filter(Boolean))) as string[];
                            if (expiries.length > 0) {
                                expiries.sort((a, b) => {
                                    const pA = a.split('-');
                                    const pB = b.split('-');
                                    const dA = new Date(Number(pA[2]||0), Number(pA[1]||1)-1, Number(pA[0]||0)).getTime();
                                    const dB = new Date(Number(pB[2]||0), Number(pB[1]||1)-1, Number(pB[0]||0)).getTime();
                                    return dA - dB;
                                });
                                finalExpiryList = expiries;
                                autoSelectedExpiry = finalExpiryList.includes(expiry || '') ? expiry : finalExpiryList[0] || '';
                            }
                        }
                    }
                        
                    // Sync available HF expiries to store so dropdown shows correct options
                    if (finalExpiryList.length > 0) {
                        const sCleanForStore = symbol.replace(/[^A-Z0-9]/g, '')
                        const canonSymForStore = (sCleanForStore === 'NIFTY' || sCleanForStore.includes('NIFTY')) ? 'NIFTY' :
                                                 (sCleanForStore === 'SENSEX' || sCleanForStore.includes('SENSEX')) ? 'SENSEX' : symbol
                        setTimeout(() => {
                            useMarketStore.getState().setExpiryForSymbol(canonSymForStore, finalExpiryList)
                        }, 0)
                    }
                    
                    // Select target expiry: use current if matches, otherwise MIN from file
                    if (autoSelectedExpiry && autoSelectedExpiry !== expiry) {
                        console.log(`[GlobalHistoryFetcher] Auto-selecting HF expiry: ${autoSelectedExpiry}`)
                        setTimeout(() => useSettingsStore.getState().setExpiry(autoSelectedExpiry), 0)
                    }

                    console.log(`[GlobalHistoryFetcher] HF Worker processing succeeded. -> ${groupedRows.length} snapshots. Available expiries: [${finalExpiryList.join(', ')}]`)
                    return groupedRows
                } catch (err: any) {
                    console.error(`[GlobalHistoryFetcher] Hugging Face failed (${err.message}). No fallback allowed in Historical mode.`);
                    throw err; // React Query will handle the error state
                }
            }

            // Mode 3: Range (Intraday or EOD from D1)
            if (
                (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') &&
                rangeStartDate &&
                rangeEndDate
            ) {
                const params = new URLSearchParams({
                    symbol,
                    start: rangeStartDate,
                    end: rangeEndDate,
                    isEod: inspectionSource === 'RangeEOD' ? 'true' : 'false',
                })
                if (expiry) params.set('expiry', expiry)
                const token = (await firebaseUser?.getIdToken()) || (await getFirebaseAuth().currentUser?.getIdToken()) || "";
                const resp = await fetch(`/api/range-history?${params}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                })
                if (!resp.ok) throw new Error(`Range history fetch failed: ${resp.status}`)
                return resp.json()
            }

            return null
        },
        enabled: !!symbol && isAuthenticated && !loading && !!firebaseUser,
        refetchInterval: (query) => (isLive && inspectionSource === 'CurrentIntraday' ? 60_000 : false),
        refetchIntervalInBackground: true,
        // staleTime just under refetchInterval so every 60s poll actually refetches from server
        staleTime: isLive && inspectionSource === 'CurrentIntraday' ? 55_000 : Infinity,
        gcTime: isLive ? 120_000 : Infinity, // Keep cache 2 min so switching tabs doesn't clear it
        placeholderData: (prev: any) => prev,
        // FREEZE FIX: Prevent React Query from retrying/refetching when data is empty.
        // Without these, empty responses are treated as "retry conditions" instead of
        // valid final states, causing repeated worker execution and fetch loops.
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    // Track last processed data to prevent redundant processing on same data reference
    const lastProcessedRef = useRef<{ data: any; symbol: string } | null>(null)
    // Track last computed key to prevent duplicate COMPUTE_ALL dispatches
    const lastComputedKeyRef = useRef<string>('')

    // Sync to appropriate store slice
    useEffect(() => {
        // FREEZE FIX: Treat null/undefined/empty as VALID FINAL STATE.
        // Previously, empty arrays caused repeated processing attempts.
        if (!data) return
        if (Array.isArray(data) && data.length === 0) {
            // Empty data is a valid final state — do NOT retry or reprocess
            return
        }

        // Identity guard: skip if we already processed this exact data+symbol combo
        if (lastProcessedRef.current?.data === data && lastProcessedRef.current?.symbol === symbol) return
        lastProcessedRef.current = { data, symbol }

        // Capture symbol at invocation time so we can abort if it changes mid-processing
        const capturedSymbol = symbol

        ;(async () => {
            try {
                let processedData = data;
                
                // Only offload to worker if we actually have compressed blobs
                // CSV fallback data is already parsed by the worker in the query step
                if (data.length > 0 && (data[0].data_blob || data[0].dataBlob)) {
                    console.time('[PERF] GlobalHistoryFetcher.WebWorkerProcess')
                    console.log(`[GlobalHistoryFetcher] Processing ${data.length} raw history entries for ${capturedSymbol} via Web Worker...`);
                    // Offload CBOR+GZIP Base64 decompression to the worker
                    processedData = await runInWorker('PROCESS_COMPRESSED', { rows: data })
                    console.timeEnd('[PERF] GlobalHistoryFetcher.WebWorkerProcess')
                } else {
                    console.log(`[GlobalHistoryFetcher] Skipping Web Worker decompression (Data is already processed)`);
                }

                // Abort if symbol changed during async processing
                if (symbol !== capturedSymbol) {
                    console.log(`[GlobalHistoryFetcher] Symbol changed (${capturedSymbol} → ${symbol}), discarding stale results.`)
                    return
                }

                console.log(`[GlobalHistoryFetcher] Successfully processed ${processedData.length} records. Syncing to DataBus (${inspectionSource}).`);
                
                // 🚀 SSOT: Dispatch COMPUTE_ALL — single Worker call for both COG + MarketWatch + AnalysisHistory
                // This replaces 3 independent Worker calls (3x Structured Clone) with 1 (1x Structured Clone)
                const computeKey = `${capturedSymbol}-${expiry}-${range}-${mode}-${processedData.length}`
                if (lastComputedKeyRef.current === computeKey) return // Already computed
                lastComputedKeyRef.current = computeKey

                const step = capturedSymbol === 'NIFTY' ? 50 : 100
                const settings = useSettingsStore.getState()
                const mwSubTab = settings.subTab || 'Combined OI'

                console.time('[PERF] GlobalHistoryFetcher.COMPUTE_ALL')
                const result = await runInWorker('COMPUTE_ALL', {
                    history: processedData,
                    symbol: capturedSymbol,
                    expiry: expiry || '',
                    step,
                    range: range || 11,
                    strikeGap: step,
                    activeRange: range || 11,
                    cogSubTab: 'Top CE / PE OI',
                    mwSubTab,
                    isVolumeMode: mode === 'VOLUME',
                    baseOpts: [],
                    mode: mode,
                    baseTimePattern: baseTime,
                })
                console.timeEnd('[PERF] GlobalHistoryFetcher.COMPUTE_ALL')

                // Abort if symbol changed during compute
                if (symbol !== capturedSymbol) return

                // 🚀 PERF: Atomic single-mutation Zustand dispatch.
                // Previously: 3 separate set() calls → 3 store notifications → 3 selector evaluations → cascade freeze.
                // Now: 1 set() call → 1 notification → deferred via setTimeout to prevent React 18 tearing.
                if (result) {
                    // Pre-compute COG dedup BEFORE entering the store mutation
                    let finalCog: any[] | null = null
                    if (result.cogHistory && Array.isArray(result.cogHistory)) {
                        const cogMap = new Map<string, any>()
                        for (const r of result.cogHistory) cogMap.set(r.time, r)
                        finalCog = Array.from(cogMap.values()).sort((a, b) => b.time.localeCompare(a.time))
                    }

                    // Map inspectionSource → store history bucket
                    const historySource = inspectionSource === 'CurrentIntraday' ? 'intraday' as const
                        : inspectionSource === 'HistoricalSnapshot' ? 'historical' as const
                        : 'range' as const

                    setTimeout(() => {
                        startTransition(() => {
                            useMarketStore.getState().setMasterComputeAll(
                                capturedSymbol,
                                historySource,
                                processedData,
                                finalCog,
                                result.marketWatchHistory || null,
                                result.analysisHistory || null
                            )
                        })
                    }, 0)
                    console.log(`[GlobalHistoryFetcher] COMPUTE_ALL completed for ${capturedSymbol}. COG: ${finalCog?.length || 0} rows, MW: ${result.marketWatchHistory?.processedHistory?.length || 0} rows`)
                }
            } catch (err) {
                console.error('[GlobalHistoryFetcher] Failed to hydrate store:', err)
            }
        })()
    }, [data, symbol, inspectionSource, expiry, range, mode, baseTime])

    return null
}
